/**
 * CoreJourneyMantra — the canonical 3-step user journey line.
 *
 *    Find the problem.  Get the solution.  Then get the right training.
 *
 * Shown across the platform to anchor every user in the same framing:
 * ownership lives with them, we provide clarity + tools.
 *
 * Placements:
 *   • Landing hero (public)
 *   • Learner / PDI landing
 *   • Lesson-review upload page
 *   • Onboarding journey
 *   • Progress / improvement surfaces
 *
 * Variants:
 *   variant="dark"   (default, on dark hero backgrounds)
 *   variant="light"  (on white cards)
 *   variant="inline" (compact, single-line)
 */
import { Search, Lightbulb, Target } from "lucide-react";

const PARTS = [
  { icon: Search,    word: "Find the problem." },
  { icon: Lightbulb, word: "Get the solution." },
  { icon: Target,    word: "Then get the right training." },
];

export default function CoreJourneyMantra({ variant = "dark", className = "", testid = "core-mantra" }) {
  if (variant === "inline") {
    return (
      <p
        data-testid={testid}
        className={`text-[11px] md:text-[12px] font-semibold tracking-wide text-slate-500 dark:text-zinc-400 ${className}`}
      >
        <span className="text-slate-700 dark:text-zinc-200">Find the problem.</span>
        {" "}Get the solution.{" "}
        <span className="text-slate-700 dark:text-zinc-200">Then get the right training.</span>
      </p>
    );
  }

  const palette = variant === "light"
    ? {
        frame: "bg-slate-50 border-slate-200 text-slate-700",
        accent: "text-indigo-600",
        divider: "bg-slate-300",
      }
    : {
        frame: "bg-white/[0.03] border-[#D4AF37]/25 text-zinc-200",
        accent: "text-[#D4AF37]",
        divider: "bg-[#D4AF37]/30",
      };

  return (
    <div
      data-testid={testid}
      className={`inline-flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border px-3 py-2 text-[12px] md:text-[13px] font-semibold ${palette.frame} ${className}`}
    >
      {PARTS.map((p, i) => {
        const Icon = p.icon;
        return (
          <span key={p.word} className="inline-flex items-center gap-1.5">
            {i > 0 && <span className={`w-1 h-1 rounded-full ${palette.divider} hidden md:inline-block`} aria-hidden />}
            <Icon className={`w-3.5 h-3.5 ${palette.accent}`} />
            <span>{p.word}</span>
          </span>
        );
      })}
    </div>
  );
}
